import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { 
  Users, BarChart3, Settings, ArrowLeft, Shield,
  UserCheck, UserX, Trash2, Edit2, Save, X,
  TrendingUp, MapPin, Activity, Calendar, DollarSign, Crown, MessageSquare
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { AdminMarketingPanel } from "../components/admin/AdminMarketingPanel";
import { AdminReviewsPanel } from "../components/admin/AdminReviewsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export default function AdminPanel() {
  const { user, token } = useAuth();
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [activeTab, setActiveTab] = useState("dashboard");
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [userSearch, setUserSearch] = useState("");
  const [userFilter, setUserFilter] = useState("all");
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
    role: "user",
    plan: "free"
  });

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
      const [usersRes, analyticsRes, settingsRes, subscriptionStatsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, authHeaders),
        axios.get(`${API}/admin/analytics`, authHeaders),
        axios.get(`${API}/admin/settings`, authHeaders),
        axios.get(`${API}/admin/subscription-stats`, authHeaders)
      ]);
      setUsers(usersRes.data);
      // Merge subscription stats revenue data into analytics
      setAnalytics({
        ...analyticsRes.data,
        revenue: {
          total: subscriptionStatsRes.data.total_revenue || 0,
          monthly: subscriptionStatsRes.data.monthly_revenue || 0,
          premium_users: subscriptionStatsRes.data.premium_users || 0,
          transactions: subscriptionStatsRes.data.total_transactions || 0
        }
      });
      setSettings(settingsRes.data);
    } catch (error) {
      toast.error("Failed to load admin data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdateUser = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, editForm);
      toast.success("User updated");
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm("Are you sure you want to delete this user?")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success("User deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { is_active: !currentStatus });
      toast.success(currentStatus ? "User deactivated" : "User activated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/admin/settings`, settings);
      toast.success("Settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { role: newRole });
      toast.success(`User role updated to ${newRole}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update user role");
    }
  };

  const handleSubscriptionChange = async (userId, newPlan) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/subscription`, { plan: newPlan });
      toast.success(`Subscription updated to ${newPlan}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update subscription");
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      await axios.post(`${API}/admin/users`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User created successfully");
      setShowCreateUser(false);
      setNewUser({ name: "", email: "", password: "", role: "user", plan: "free" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  if (user?.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
          <p className="text-white/60 mb-4">You don&apos;t have permission to access this page.</p>
          <Link to="/">
            <Button className="bg-white text-black hover:bg-white/90">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] p-6 md:p-8">
      {/* Header */}
      <header className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon" className="rounded-xl hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-black tracking-tight flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#ff0080]" />
              Admin Panel
            </h1>
            <p className="text-white/50 text-sm">Manage users, analytics & settings</p>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="glass rounded-xl p-1 w-fit">
          <TabsTrigger 
            value="dashboard" 
            className="rounded-lg data-[state=active]:bg-white/10"
            data-testid="admin-tab-dashboard"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger 
            value="users" 
            className="rounded-lg data-[state=active]:bg-white/10"
            data-testid="admin-tab-users"
          >
            <Users className="w-4 h-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger 
            value="settings" 
            className="rounded-lg data-[state=active]:bg-white/10"
            data-testid="admin-tab-settings"
          >
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </TabsTrigger>
          <TabsTrigger 
            value="reviews" 
            className="rounded-lg data-[state=active]:bg-white/10"
            data-testid="admin-tab-reviews"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Reviews
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard">
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {/* Stats Cards */}
            <motion.div variants={item} className="glass rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-xl bg-[#00f2ea]/10">
                  <Users className="w-5 h-5 text-[#00f2ea]" />
                </div>
                <span className="text-white/60">Total Users</span>
              </div>
              <p className="text-4xl font-black">{analytics?.users?.total || 0}</p>
              <p className="text-sm text-white/40 mt-1">
                +{analytics?.users?.recent_signups || 0} this week
              </p>
            </motion.div>

            <motion.div variants={item} className="glass rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-xl bg-[#ccff00]/10">
                  <UserCheck className="w-5 h-5 text-[#ccff00]" />
                </div>
                <span className="text-white/60">Active Users</span>
              </div>
              <p className="text-4xl font-black">{analytics?.users?.active || 0}</p>
              <p className="text-sm text-white/40 mt-1">
                {analytics?.users?.admins || 0} admins
              </p>
            </motion.div>

            <motion.div variants={item} className="glass rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-xl bg-[#ff0080]/10">
                  <MapPin className="w-5 h-5 text-[#ff0080]" />
                </div>
                <span className="text-white/60">Saved Locations</span>
              </div>
              <p className="text-4xl font-black">{analytics?.locations?.total || 0}</p>
            </motion.div>

            <motion.div variants={item} className="glass rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-xl bg-[#7000ff]/10">
                  <Activity className="w-5 h-5 text-[#7000ff]" />
                </div>
                <span className="text-white/60">API Calls (24h)</span>
              </div>
              <p className="text-4xl font-black">{analytics?.api?.calls_24h || 0}</p>
            </motion.div>

            {/* Revenue & Profit Section */}
            <motion.div variants={item} className="glass rounded-2xl p-6 md:col-span-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20">
              <h3 className="font-semibold mb-4 flex items-center gap-2 text-green-400">
                <DollarSign className="w-5 h-5" />
                Revenue & Profit
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
                <div className="text-center">
                  <p className="text-xs text-white/50 uppercase mb-1">Total Revenue</p>
                  <p className="text-3xl font-black text-green-400">
                    ${(analytics?.revenue?.total || 0).toFixed(2)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-white/50 uppercase mb-1">This Month</p>
                  <p className="text-3xl font-black text-[#00f2ea]">
                    ${(analytics?.revenue?.monthly || analytics?.revenue?.total || 0).toFixed(2)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-white/50 uppercase mb-1">Premium Users</p>
                  <p className="text-3xl font-black text-yellow-400">
                    {analytics?.revenue?.premium_users || 0}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-white/50 uppercase mb-1">Transactions</p>
                  <p className="text-3xl font-black text-purple-400">
                    {analytics?.revenue?.transactions || 0}
                  </p>
                </div>
              </div>
              
              {/* Profit breakdown */}
              <div className="pt-4 border-t border-white/10">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-white/50">Gross Revenue</p>
                    <p className="font-bold text-green-400 text-lg">${(analytics?.revenue?.total || 0).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-white/50">PayPal Fees (~3%)</p>
                    <p className="font-bold text-red-400 text-lg">-${((analytics?.revenue?.total || 0) * 0.03).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-white/50">Net Profit</p>
                    <p className="font-bold text-[#00f2ea] text-lg">${((analytics?.revenue?.total || 0) * 0.97).toFixed(2)}</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <AdminMarketingPanel token={token} isDarkMode={isDarkMode} />

            {/* Signup Chart */}
            <motion.div variants={item} className="glass rounded-2xl p-6 md:col-span-2">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#00f2ea]" />
                User Signups (Last 7 Days)
              </h3>
              <div className="h-48 w-full min-w-0 flex items-end gap-3" data-testid="admin-signup-chart-container">
                {(analytics?.signups_by_day || []).map((day) => {
                  const maxSignupCount = Math.max(1, ...(analytics?.signups_by_day || []).map((entry) => entry.count || 0));
                  const height = `${Math.max(8, ((day.count || 0) / maxSignupCount) * 150)}px`;
                  return (
                    <div key={day.date} className="flex-1 h-full flex flex-col justify-end items-center gap-2" data-testid={`admin-signup-bar-${day.date}`}>
                      <div className="w-full max-w-10 rounded-t-lg bg-gradient-to-t from-[#00f2ea]/45 to-[#00f2ea]" style={{ height }} title={`${day.count || 0} signups`} />
                      <span className="text-[10px] text-white/40">{new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}</span>
                    </div>
                  );
                })}
              </div>
            </motion.div>

            {/* Popular Locations */}
            <motion.div variants={item} className="glass rounded-2xl p-6 md:col-span-2">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#ff0080]" />
                Popular Locations
              </h3>
              <div className="h-48 w-full min-w-0 space-y-3" data-testid="admin-popular-locations-chart-container">
                {(analytics?.locations?.popular?.slice(0, 5) || []).length === 0 ? (
                  <p className="text-sm text-white/40" data-testid="admin-popular-locations-empty">No saved locations yet</p>
                ) : (analytics?.locations?.popular?.slice(0, 5) || []).map((locationItem) => {
                  const locations = analytics?.locations?.popular?.slice(0, 5) || [];
                  const maxLocationCount = Math.max(1, ...locations.map((entry) => entry.count || 0));
                  return (
                    <div key={locationItem.name} className="grid grid-cols-[120px_1fr_32px] items-center gap-3" data-testid={`admin-location-bar-${locationItem.name}`}>
                      <span className="text-xs text-white/60 truncate">{locationItem.name}</span>
                      <div className="h-3 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full rounded-full bg-[#ff0080]" style={{ width: `${Math.max(4, ((locationItem.count || 0) / maxLocationCount) * 100)}%` }} />
                      </div>
                      <span className="text-xs text-white/45 text-right">{locationItem.count || 0}</span>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        </TabsContent>

        {/* Users Tab - Unified User Management */}
        <TabsContent value="users">
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="space-y-6"
          >
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="glass rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-blue-400" />
                  <span className="text-sm opacity-60">Total Users</span>
                </div>
                <p className="text-3xl font-bold">{users.length}</p>
              </div>
              <div className="glass rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="w-5 h-5 text-yellow-400" />
                  <span className="text-sm opacity-60">Premium</span>
                </div>
                <p className="text-3xl font-bold text-green-400">
                  {users.filter(u => u.subscription?.plan === 'premium').length}
                </p>
              </div>
              <div className="glass rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-5 h-5 text-[#ff0080]" />
                  <span className="text-sm opacity-60">Admins</span>
                </div>
                <p className="text-3xl font-bold text-[#ff0080]">
                  {users.filter(u => u.role === 'admin').length}
                </p>
              </div>
              <div className="glass rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-[#00f2ea]" />
                  <span className="text-sm opacity-60">Conversion</span>
                </div>
                <p className="text-3xl font-bold text-purple-400">
                  {users.length > 0 ? ((users.filter(u => u.subscription?.plan === 'premium').length / users.length) * 100).toFixed(1) : 0}%
                </p>
              </div>
            </div>

            {/* Search & Filter */}
            <div className="glass rounded-2xl p-4">
              <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="relative w-full md:w-96">
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-[#00f2ea]/50"
                  />
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40" />
                </div>
                <div className="flex gap-2 flex-wrap items-center">
                  {['all', 'premium', 'free', 'admin'].map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setUserFilter(filter)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        userFilter === filter
                          ? 'bg-[#00f2ea] text-black'
                          : 'bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      {filter.charAt(0).toUpperCase() + filter.slice(1)}
                    </button>
                  ))}
                  <Button
                    onClick={() => setShowCreateUser(true)}
                    className="bg-[#00f2ea] hover:bg-[#00f2ea]/80 text-black font-semibold ml-2"
                  >
                    <UserCheck className="w-4 h-4 mr-2" />
                    Create User
                  </Button>
                </div>
              </div>
            </div>

            {/* Unified User Table */}
            <div className="glass rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="text-left p-4 font-semibold text-white/60">User</th>
                      <th className="text-left p-4 font-semibold text-white/60">Email</th>
                      <th className="text-left p-4 font-semibold text-white/60">Role</th>
                      <th className="text-left p-4 font-semibold text-white/60">Subscription</th>
                      <th className="text-left p-4 font-semibold text-white/60">Status</th>
                      <th className="text-left p-4 font-semibold text-white/60">Joined</th>
                      <th className="text-right p-4 font-semibold text-white/60">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users
                      .filter(u => {
                        // Search filter
                        if (userSearch) {
                          const search = userSearch.toLowerCase();
                          if (!u.name?.toLowerCase().includes(search) && !u.email?.toLowerCase().includes(search)) {
                            return false;
                          }
                        }
                        // Type filter
                        if (userFilter === 'premium') return u.subscription?.plan === 'premium';
                        if (userFilter === 'free') return !u.subscription?.plan || u.subscription?.plan === 'free';
                        if (userFilter === 'admin') return u.role === 'admin';
                        return true;
                      })
                      .map((u, idx) => (
                      <motion.tr 
                        key={u.id}
                        variants={item}
                        className="border-t border-white/5 hover:bg-white/5"
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                              u.subscription?.plan === 'premium' 
                                ? 'bg-gradient-to-br from-yellow-400 to-orange-500' 
                                : 'bg-gradient-to-br from-[#00f2ea] to-[#7000ff]'
                            }`}>
                              {(u.name || u.email || '?').charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <span className="font-medium">{u.name || 'No Name'}</span>
                              {u.role === 'admin' && (
                                <span className="ml-2 text-xs bg-[#ff0080]/20 text-[#ff0080] px-2 py-0.5 rounded-full">Admin</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="p-4 text-white/60">{u.email}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            u.role === 'admin' ? 'bg-[#ff0080]/20 text-[#ff0080]' : 'bg-white/10 text-white/60'
                          }`}>
                            {u.role || 'user'}
                          </span>
                        </td>
                        <td className="p-4">
                          <select
                            value={u.subscription?.plan || 'free'}
                            onChange={(e) => handleSubscriptionChange(u.id, e.target.value)}
                            className={`px-3 py-1 rounded-lg text-xs font-medium cursor-pointer ${
                              u.subscription?.plan === 'premium' 
                                ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                                : u.subscription?.plan === 'trial'
                                ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                                : 'bg-white/10 text-white/60 border border-white/10'
                            }`}
                          >
                            <option value="free">Free</option>
                            <option value="trial">Trial</option>
                            <option value="premium">Premium</option>
                          </select>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            u.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                          }`}>
                            {u.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="p-4 text-white/60 text-sm">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingUser(u.id);
                                setEditForm({ name: u.name, email: u.email });
                              }}
                              className="rounded-lg hover:bg-white/10"
                              data-testid={`edit-user-${u.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRoleChange(u.id, u.role === 'admin' ? 'user' : 'admin')}
                              className={`rounded-lg ${u.role === 'admin' ? 'text-[#ff0080] hover:bg-[#ff0080]/20' : 'hover:bg-white/10'}`}
                              data-testid={`toggle-role-${u.id}`}
                            >
                              <Shield className="w-4 h-4" />
                            </Button>
                            {u.id !== user?.id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteUser(u.id)}
                                className="rounded-lg hover:bg-red-500/20 text-red-400"
                                data-testid={`delete-user-${u.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="p-4 border-t border-white/5 text-sm text-white/40">
                Showing {users.filter(u => {
                  if (userSearch) {
                    const search = userSearch.toLowerCase();
                    if (!u.name?.toLowerCase().includes(search) && !u.email?.toLowerCase().includes(search)) return false;
                  }
                  if (userFilter === 'premium') return u.subscription?.plan === 'premium';
                  if (userFilter === 'free') return !u.subscription?.plan || u.subscription?.plan === 'free';
                  if (userFilter === 'admin') return u.role === 'admin';
                  return true;
                }).length} of {users.length} users
              </div>
            </div>
          </motion.div>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="max-w-2xl space-y-6"
          >
            <motion.div variants={item} className="glass rounded-2xl p-6 space-y-6">
              <h3 className="font-semibold text-lg">General Settings</h3>
              
              <div className="space-y-2">
                <Label>Site Name</Label>
                <Input
                  value={settings?.site_name || ""}
                  onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
                  className="bg-white/5 border-white/10 rounded-xl"
                  data-testid="settings-site-name"
                />
              </div>

              <div className="space-y-2">
                <Label>Default Temperature Unit</Label>
                <Select
                  value={settings?.default_temp_unit || "fahrenheit"}
                  onValueChange={(value) => setSettings({ ...settings, default_temp_unit: value })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 rounded-xl" data-testid="settings-temp-unit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fahrenheit">Fahrenheit (°F)</SelectItem>
                    <SelectItem value="celsius">Celsius (°C)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Allow Registration</Label>
                  <p className="text-sm text-white/50">Allow new users to register</p>
                </div>
                <Switch
                  checked={settings?.allow_registration ?? true}
                  onCheckedChange={(checked) => setSettings({ ...settings, allow_registration: checked })}
                  data-testid="settings-allow-registration"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Maintenance Mode</Label>
                  <p className="text-sm text-white/50">Temporarily disable the site</p>
                </div>
                <Switch
                  checked={settings?.maintenance_mode ?? false}
                  onCheckedChange={(checked) => setSettings({ ...settings, maintenance_mode: checked })}
                  data-testid="settings-maintenance-mode"
                />
              </div>

              <Button 
                onClick={handleSaveSettings}
                className="w-full bg-white text-black hover:bg-white/90 rounded-xl"
                data-testid="save-settings-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </Button>
            </motion.div>
          </motion.div>
        </TabsContent>

        <TabsContent value="reviews">
          <AdminReviewsPanel token={token} isDarkMode={isDarkMode} />
        </TabsContent>
      </Tabs>

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent className="glass border-white/10" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update this user&apos;s profile, role, and account status.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={editForm.name || ""}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="bg-white/5 border-white/10 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                value={editForm.email || ""}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="bg-white/5 border-white/10 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                value={editForm.role || "user"}
                onValueChange={(value) => setEditForm({ ...editForm, role: value })}
              >
                <SelectTrigger className="bg-white/5 border-white/10 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3">
              <Button
                variant="ghost"
                onClick={() => setEditingUser(null)}
                className="flex-1 rounded-xl"
                data-testid="edit-user-cancel-button"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleUpdateUser(editingUser)}
                className="flex-1 bg-white text-black hover:bg-white/90 rounded-xl"
                data-testid="edit-user-save-button"
              >
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create User Modal */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent className="glass border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-[#00f2ea]" />
              Create New User
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="John Doe"
                className="bg-white/5 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="john@example.com"
                className="bg-white/5 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Password *</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="••••••••"
                className="bg-white/5 border-white/10"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Role</Label>
                <Select
                  value={newUser.role}
                  onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Subscription</Label>
                <Select
                  value={newUser.plan}
                  onValueChange={(value) => setNewUser({ ...newUser, plan: value })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="ghost"
                onClick={() => setShowCreateUser(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateUser}
                className="bg-[#00f2ea] hover:bg-[#00f2ea]/80 text-black"
              >
                <UserCheck className="w-4 h-4 mr-2" />
                Create User
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
